import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '@/lib/cn';

/**
 * Form controls share one shell so focus, error and disabled states are
 * identical across the app. Errors are announced, associated via
 * `aria-describedby`, and marked with `aria-invalid` — colour is never the only
 * carrier of the message.
 */
/**
 * `text-md` (16px) below `lg` is not a style choice: iOS Safari zooms the
 * viewport whenever a focused control is smaller than 16px and never zooms
 * back, which strands the trader scrolled sideways mid-form. The dense 14px
 * control text is kept for the desktop layout only. A `pointer: coarse` rule in
 * app.css enforces the same floor for touch devices wide enough to hit `lg`.
 */
const CONTROL_BASE =
  'focus-ring w-full rounded-md border bg-surface px-2.5 text-md text-fg lg:text-base ' +
  'transition-colors duration-fast ease-out placeholder:text-fg-muted ' +
  'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-fg-muted';

/** 44px on touch, dense on desktop — see `SIZES` in Button for the same rule. */
const CONTROL_HEIGHT = 'h-11 lg:h-9';

const CONTROL_TONE = {
  normal: 'border-line-strong hover:border-fg-faint',
  invalid: 'border-danger-500 hover:border-danger-700',
} as const;

interface FieldShellProps {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
  /** Rendered on the label row, right-aligned (e.g. "Forgot password?"). */
  aside?: ReactNode;
}

export function FieldShell({
  label,
  htmlFor,
  error,
  hint,
  required,
  children,
  className,
  aside,
}: FieldShellProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={htmlFor} className="text-sm font-medium text-fg-secondary">
          {label}
          {required ? (
            <span className="ml-0.5 text-danger-500" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
        {aside}
      </div>

      {children}

      {error ? (
        <p id={`${htmlFor}-error`} className="text-xs text-danger-500">
          {error}
        </p>
      ) : hint ? (
        <p id={`${htmlFor}-hint`} className="text-xs text-fg-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function describedBy(id: string, error?: string, hint?: ReactNode): string | undefined {
  if (error) return `${id}-error`;
  if (hint) return `${id}-hint`;
  return undefined;
}

// ---------- Text input ----------

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  error?: string;
  hint?: ReactNode;
  aside?: ReactNode;
  containerClassName?: string;
  /** Money, card numbers and references get tabular figures. */
  monospace?: boolean;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, aside, containerClassName, monospace, className, required, ...rest },
  ref,
) {
  const id = useId();

  return (
    <FieldShell
      label={label}
      htmlFor={id}
      error={error}
      hint={hint}
      aside={aside}
      required={required}
      className={containerClassName}
    >
      <input
        ref={ref}
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        className={cn(
          CONTROL_BASE,
          CONTROL_HEIGHT,
          error ? CONTROL_TONE.invalid : CONTROL_TONE.normal,
          monospace && 'num',
          className,
        )}
        {...rest}
      />
    </FieldShell>
  );
});

// ---------- Textarea ----------

export interface TextAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label: string;
  error?: string;
  hint?: ReactNode;
  containerClassName?: string;
}

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  function TextAreaField(
    { label, error, hint, containerClassName, className, required, rows = 4, ...rest },
    ref,
  ) {
    const id = useId();

    return (
      <FieldShell
        label={label}
        htmlFor={id}
        error={error}
        hint={hint}
        required={required}
        className={containerClassName}
      >
        <textarea
          ref={ref}
          id={id}
          rows={rows}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, error, hint)}
          className={cn(
            CONTROL_BASE,
            'min-h-11 resize-y py-2 leading-normal',
            error ? CONTROL_TONE.invalid : CONTROL_TONE.normal,
            className,
          )}
          {...rest}
        />
      </FieldShell>
    );
  },
);

// ---------- Select ----------

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string;
  error?: string;
  hint?: ReactNode;
  containerClassName?: string;
  children: ReactNode;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, error, hint, containerClassName, className, required, children, ...rest },
  ref,
) {
  const id = useId();

  return (
    <FieldShell
      label={label}
      htmlFor={id}
      error={error}
      hint={hint}
      required={required}
      className={containerClassName}
    >
      <select
        ref={ref}
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        className={cn(
          CONTROL_BASE,
          CONTROL_HEIGHT,
          'appearance-none bg-surface pr-8',
          error ? CONTROL_TONE.invalid : CONTROL_TONE.normal,
          className,
        )}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><path d='m6 9 6 6 6-6'/></svg>\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 0.6rem center',
        }}
        {...rest}
      >
        {children}
      </select>
    </FieldShell>
  );
});

// ---------- Checkbox / toggle row ----------

export interface CheckRowProps {
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}

export function CheckRow({ label, description, checked, disabled, onChange }: CheckRowProps) {
  const id = useId();

  return (
    <div className="flex items-start gap-3 py-3">
      {/*
        The visual box stays at the native 16px, but a 16px target is unhittable
        with a thumb. `::after` does not render on a replaced <input>, so the
        44x44 hit area is carried by a wrapping label instead — it points at the
        same control, contributes no text to the accessible name, and its
        overflow only ever covers the row's own text label, which toggles the
        same checkbox.
      */}
      <label
        htmlFor={id}
        className={cn(
          'tap-target mt-0.5 flex shrink-0',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
          aria-describedby={description ? `${id}-description` : undefined}
          className={cn(
            'focus-ring h-4 w-4 shrink-0 rounded-sm',
            'border border-line-strong accent-accent-600',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        />
      </label>
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="block cursor-pointer text-base font-medium text-fg">
          {label}
        </label>
        {description ? (
          <p id={`${id}-description`} className="text-sm text-fg-muted">
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
}
